import React, { FC, useState, useEffect } from "react";
import { Button } from "antd";
import { getTargetLanguageDictListSimpleApi } from "../../service/dict";
import { useAlert } from "../../hooks/useAlert";

export interface TargetLanguageSelectorProps {
  /** 已选语言代码列表 */
  value: string[];
  onChange: (langCodes: string[]) => void;
  /** 最多可选数量，默认 5 */
  maxCount?: number;
  /** 表单项 label 文案（如需，可由外层再配合样式/必填标识） */
  label?: string;
  /** 额外容器类名，便于不同场景定制样式 */
  className?: string;
  /** 语言列表（传入则直接使用；不传则自动从接口获取，接口失败 fallback 到静态列表） */
  languages?: Record<string, string>;
  /** 禁用的语言 code 列表（显示但不可选） */
  disabledCodes?: string[];
}

/** 目标语言多选（与新增语种弹窗同款样式，限制最多选中数量） */
export const TargetLanguageSelector: FC<TargetLanguageSelectorProps> = ({
  value,
  onChange,
  maxCount = 5,
  label = "目标语言",
  className = "",
  languages,
  disabledCodes = [],
}) => {
  const { showWarning } = useAlert();
  const [fetchedLanguages, setFetchedLanguages] = useState<Record<string, string> | null>(null);

  // 未传 languages 时，自动从接口获取
  useEffect(() => {
    if (languages) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getTargetLanguageDictListSimpleApi();
        if (cancelled) return;
        if (res?.code === 0 && Array.isArray(res.data) && res.data.length > 0) {
          const record: Record<string, string> = {};
          for (const row of res.data) {
            const code = String(row.value ?? "").trim();
            if (code) record[code] = String(row.name ?? code).trim() || code;
          }
          setFetchedLanguages(Object.keys(record).length > 0 ? record : null);
        }
      } catch {
        // 接口失败，fallback 到静态列表
      }
    })();
    return () => { cancelled = true; };
  }, [languages]);

  const allLanguagesRecord = languages ?? fetchedLanguages ?? {};
  const disabledSet = new Set(disabledCodes.map((c) => String(c || "").trim()).filter(Boolean));
  const availableLanguages = Object.entries(allLanguagesRecord).map(([code, label]) => ({
    code,
    label,
  }));

  const toggle = (code: string) => {
    if (disabledSet.has(code)) return;
    if (value.includes(code)) {
      onChange(value.filter((c) => c !== code));
      return;
    }
    if (value.length >= maxCount) {
      showWarning(`最多只能选择${maxCount}个翻译语种`);
      return;
    }
    onChange([...value, code]);
  };

  return (
    <div className={`${className || ""}`.trim()}>
      <div className="mb-0">{label}</div>
      <div className="mt-2 flex flex-wrap gap-2.5">
        {availableLanguages.map((lang) => {
          const active = value.includes(lang.code);
          const disabled = disabledSet.has(lang.code);
          return (
            <Button
              key={lang.code}
              type={active ? "primary" : "default"}
              size="small"
              title={lang.label}
              disabled={disabled}
              onClick={() => toggle(lang.code)}
              style={{ padding: "4px 12px", height: 28, lineHeight: "20px" }}
            >
              {lang.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};
